<template>
  <div>
    <div :class="modalWidgetDynamicClass">
      <div @scroll="handleScroll" class="live-bytes__wrapper hide-scrollbar">
        <div class="live-bytes__header">
          <a-image
            :src="$helper.getLinkToIlBucketFile('live-bytes-logo-v3.svg')"
            alt="live bytes"
            class="live-bytes__logo"
          />
          <a-view-more
            v-if="!isInModal"
            class="live-bytes__view-more"
            :view-more-style="$options.consts.VIEW_MORE_STYLE.LINK"
            @click="openLiveBytesModal"
          />
          <div v-if="isInModal">
            <a-icon
              :width="30"
              :height="30"
              class="live-bytes__modal-close-icon"
              is-button
              :icon="$options.consts.ICON.CROSS_IN_CIRCLE"
              @click="closeLiveBytesModal"
            />
          </div>
        </div>
        <a-spinner v-if="showModalInSpinner" disable-transition />
        <div v-else class="live-bytes__bytes">
          <fade-transition group :duration="300">
            <live-byte
              v-for="liveByte in liveBytesToRender"
              :key="liveByte.Id"
              :title="liveByte.Title"
              :source-name="liveByte.SourceName"
              :created-on="liveByte.CreatedOn"
              :time-refresh-key="timeRefreshKey"
              :origin="liveByte.Origin"
              :image="liveByte.Image"
              :class="getLiveByteClasses(liveByte)"
              @open-image-modal="openImageModal(liveByte.Image.URL)"
            />
          </fade-transition>
        </div>
      </div>
    </div>
    <a-image-download-modal
      v-if="popupImage"
      :image="popupImage"
      :modal-name="modalName"
      class="live-bytes__image-modal"
    />
  </div>
</template>

<script>
import LiveByte from './LiveByte'
import AIcon, { ICON } from 'shared/AIcon'
import AViewMore from 'shared/AViewMore'
import AImageDownloadModal from 'shared/AImageDownloadModal'
import { mapGetters, mapMutations } from 'vuex'
import { PROP_TYPES, propValidator } from '@/utils/validators'
import ASpinner from 'shared/ASpinner'
import { VIEW_MORE_STYLE } from 'shared/AViewMore/enums'
import * as types from '~/store/mutation-types'
import { MODAL } from '~/components/_modals/AModalWrapper/index.vue'

const LIVE_BYTES_TO_DISPLAY = 7
const LIVE_BYTES_TO_DISPLAY_MODAL = 50
const SCROLL_BOTTOM_THRESHOLD = 5

const TIME_REFRESH_KEY_UPDATE_INTERVAL = 1000 * 60

export default {
  name: 'LiveBytes',
  components: { AViewMore, LiveByte, AIcon, ASpinner, AImageDownloadModal },
  consts: { ICON, VIEW_MORE_STYLE, MODAL },
  props: {
    isInModal: propValidator([PROP_TYPES.BOOLEAN], false, false)
  },
  data() {
    return {
      timeRefreshKey: this.$helper.guid(),
      clearInterval: null,
      isLoading: false,
      popupImage: { url: null }
    }
  },
  computed: {
    ...mapGetters({
      liveBytes: 'liveBytes/liveBytesItems'
    }),
    modalWidgetDynamicClass() {
      return {
        'live-bytes__modal': this.isInModal,
        'live-bytes__widget': !this.isInModal
      }
    },
    liveBytesToRender() {
      const displayLimit = this.isInModal
        ? LIVE_BYTES_TO_DISPLAY_MODAL
        : LIVE_BYTES_TO_DISPLAY
      return this.liveBytes.slice(0, displayLimit)
    },
    showModalInSpinner() {
      return this.isInModal && this.isLoading
    },
    modalName() {
      return this.isInModal
        ? MODAL.LIVEBYTES_MODAL_IMAGE_DOWNLOAD
        : MODAL.LIVEBYTES_IMAGE_DOWNLOAD
    }
  },
  methods: {
    ...mapMutations({
      setLiveBytes: `liveBytes/${types.SET_LIVE_BYTES_ITEMS}`
    }),
    getLiveByteClasses(liveByte) {
      return ['live-bytes__byte', { 'live-bytes__new-byte': liveByte.isNew }]
    },
    updateTimeRefreshKey() {
      this.timeRefreshKey = this.$helper.guid()
    },
    openLiveBytesModal() {
      this.$bus.$emit('open-modal-livebytes')
    },
    closeLiveBytesModal() {
      this.$bus.$emit('close-modal-livebytes')
    },
    handleScroll(event) {
      const el = event.target

      const isScrollAtBottom =
        el.scrollTop + el.clientHeight >=
        el.scrollHeight - SCROLL_BOTTOM_THRESHOLD

      this.$emit('scroll-state-changed', { isScrollAtBottom })
    },
    startRefreshKeyInterval() {
      const { clearInterval } = this.$helper.runWithInterval(
        this.updateTimeRefreshKey,
        TIME_REFRESH_KEY_UPDATE_INTERVAL
      )

      this.clearInterval = clearInterval
    },
    clearLiveBytesIsNew() {
      const updatedItems = this.liveBytes.map(liveByte => ({
        ...liveByte,
        isNew: false
      }))
      this.setLiveBytes(updatedItems)
    },
    openImageModal(imageUrl) {
      this.popupImage.url = imageUrl
      this.$nextTick(() => {
        this.$helper.openModal(this.modalName)
      })
    }
  },
  mounted() {
    this.startRefreshKeyInterval()
    this.clearLiveBytesIsNew()
  },
  beforeDestroy() {
    this.$helper.closeModal(this.modalName)
    if (this.clearInterval) {
      this.clearInterval()
    }
  }
}
</script>

<style lang="scss">
.live-bytes__modal {
  height: 100vh;
  padding: 14px 24px 24px;

  .live-bytes__wrapper {
    overflow-y: auto;
    height: 100%;
  }

  .live-bytes__modal-close-icon {
    fill: $c--main;
  }

  .live-bytes__header {
    padding-top: 10px;
    padding-bottom: 18px;
    margin-bottom: 18px;
  }
}

.live-bytes__widget {
  max-height: 667px;
  position: relative;

  @include tablet {
    max-height: 508px;
  }

  .live-bytes__header {
    padding-bottom: 18px;
    margin-bottom: 18px;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100px;
    background: linear-gradient(
      to bottom,
      rgba($c--white, 0) 0%,
      rgba($c--white, 0.7) 50%,
      rgba($c--white, 0.95) 100%
    );
    pointer-events: none;
    z-index: 1;
  }

  .live-bytes__wrapper {
    max-height: 667px;
    overflow-y: auto;
    position: relative;

    @include tablet {
      max-height: 508px;
    }
  }
}

.live-bytes__header {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  background: $c--white;
  border-bottom: 1px solid $c--gray-light;

  .a-image__wrapper {
    width: auto;
  }
}

.live-bytes__logo {
  width: 132px;
}

.live-bytes__byte {
  &:not(:last-child) {
    margin-bottom: 8px;
  }
}

.live-bytes__new-byte {
  /*
    Format: animation: name duration timing-function delay iteration-count fill-mode;
    - name: flash-anim
    - duration: 3s
    - timing-function: ease
    - delay: 150ms (0.15s)
    - iteration-count: 1 (just once)
    - fill-mode: forwards (keeps final state)
  */
  animation: flash-anim 4s ease 0s 1 forwards;
}

@keyframes flash-anim {
  0% {
    background-color: rgba($c--main, 0.4);
  }
  100% {
    background-color: transparent;
  }
}
</style>
